import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Search, User, Shield, CheckCircle2, AlertTriangle, Clock3, MinusCircle, FileText, XCircle, Ban, Globe, ArrowRight, ScanLine, FileSearch, Database, BadgeCheck, Copy, Check } from "lucide-react";
import { api, qrUrl, SITE_ORIGIN } from "../lib/api";

const WRAP = "mx-auto max-w-5xl px-4 sm:px-8";

// Map the entity's raw registry status to honest, status-aware presentation.
// A found record is always an OFFICIAL registry record; status tells its current standing.
const STATUS_META = {
  ACTIVE:      { tone: "green", Icon: CheckCircle2, word: "Active",      note: "This credential is currently active in the WSTF registry." },
  PROVISIONAL: { tone: "amber", Icon: Clock3,       word: "Provisional", note: "Registered on a provisional basis — not full membership." },
  SUSPENDED:   { tone: "red",   Icon: AlertTriangle, word: "Suspended",  note: "An official record exists, but the credential is currently suspended." },
  INACTIVE:    { tone: "slate", Icon: MinusCircle,  word: "Inactive",    note: "An official record exists, but the credential is currently inactive." },
  DRAFT:       { tone: "slate", Icon: FileText,     word: "Draft",       note: "This record is a draft and not yet fully registered." },
};
const FALLBACK_STATUS = { tone: "slate", Icon: MinusCircle, word: "On record", note: "An official WSTF registry record exists for this credential." };
const TONE = {
  green: "text-emerald-400 border-emerald-500/50 bg-emerald-950/30",
  amber: "text-amber-300 border-amber-500/50 bg-amber-950/30",
  red:   "text-rose-400 border-rose-500/50 bg-rose-950/30",
  slate: "text-slate-300 border-slate-600 bg-slate-900/40",
};

// Accepts legacy WSBF- IDs too (printed cards/QR); the API resolves either prefix.
function classify(v) {
  if (!/^WS[BT]F-/.test(v)) return { invalid: true };
  if (/^WS[BT]F-CLUB-/.test(v)) return { kind: "club" };
  if (/^WS[BT]F-NT-/.test(v)) return { unsupported: "National Team" };
  if (/^WS[BT]F-FED-/.test(v)) return { unsupported: "Federation" };
  if (/^WS[BT]F-[A-Z]{3}-\d{3,6}$/.test(v)) return { kind: "player" };
  return { invalid: true };
}

export default function Verify() {
  const { type, id } = useParams();
  const nav = useNavigate();
  const [query, setQuery] = useState(id || "");
  const [phase, setPhase] = useState("idle"); // idle | checking | done
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const lookup = useCallback(async (kind, value) => {
    setPhase("checking");
    setResult(null);
    try {
      const { data } = await api.get(`/verify/${kind}/${value}`);
      if (data.found) setResult({ kind: "FOUND", data });
      else setResult({ kind: "NOT_FOUND", id: value });
    } catch {
      setResult({ kind: "ERROR", id: value }); // API/network failure ≠ invalid credential
    } finally {
      setPhase("done");
    }
  }, []);

  useEffect(() => {
    if (type && id) { setQuery(id); lookup(type, id); }
  }, [type, id, lookup]);

  const submit = (e) => {
    e.preventDefault();
    const v = query.trim().toUpperCase();
    if (!v) return;
    const c = classify(v);
    if (c.invalid) { setResult({ kind: "INVALID", id: v }); setPhase("done"); return; }
    if (c.unsupported) { setResult({ kind: "UNSUPPORTED", id: v, which: c.unsupported }); setPhase("done"); return; }
    nav(`/verify/${c.kind}/${v}`); // deep-link → effect runs the lookup, lands populated (for QR too)
  };

  const copyShare = (url) => {
    try { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1700); }
    catch { window.prompt("Copy this verification link:", url); }
  };

  return (
    <div className="animate-fade-up pb-24 overflow-x-hidden">
      {/* ---- INSTITUTIONAL HERO ---- */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 security-grid opacity-[0.5] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(55% 120% at 50% -20%, rgba(212,175,55,0.16) 0%, transparent 58%)" }} />
        <div className={`${WRAP} relative py-9 sm:py-11 text-center`}>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-[#E5B842] font-mono font-bold mb-3">
            <BadgeCheck className="w-3.5 h-3.5" /> WSTF Credential Authority
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none">Verification Terminal</h1>
          <p className="mt-4 max-w-xl mx-auto text-sm text-slate-400">Enter or scan a WSTF ID to confirm its official registry standing. Verification checks the identifier against the public WSTF registry and returns the current public status of the record.</p>
        </div>
      </section>

      <div className={`${WRAP} pt-8`}>
        {/* ---- PRIMARY CONTROL ---- */}
        <form onSubmit={submit} className="mb-4" data-testid="verify-form">
          <label htmlFor="verify-id" className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">WSTF Credential ID</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-5 h-5 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input id="verify-id" data-testid="verify-input" value={query} autoComplete="off" autoCapitalize="characters" spellCheck="false"
                aria-describedby="verify-msg"
                onChange={(e) => setQuery(e.target.value)}
                placeholder="WSTF-GRE-000001 or WSTF-CLUB-GRE-0001"
                className="w-full bg-[#0f0f0f] border border-[#2b2512] rounded-xl pl-12 pr-4 py-4 text-slate-100 font-mono uppercase tracking-wider focus:outline-none focus:border-[#E5B842]/60"
                style={{ transition: "border-color 0.15s ease" }} />
            </div>
            <button data-testid="verify-submit" type="submit"
              className="shrink-0 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-[#E5B842] text-[#000000] font-heading font-black uppercase text-sm tracking-widest hover:bg-[#C99E30]"
              style={{ transition: "background-color 0.15s ease" }}>
              Verify <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {/* example credentials */}
          <div className="flex flex-wrap items-center gap-2 mt-3" id="verify-msg">
            <span className="text-[10px] uppercase tracking-wider text-slate-600 font-mono">Try:</span>
            <button type="button" data-testid="verify-example-player" onClick={() => { setQuery("WSTF-GRE-000001"); nav("/verify/player/WSTF-GRE-000001"); }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-[#2b2512] text-[11px] font-mono text-slate-300 hover:border-[#E5B842]/50 hover:text-[#E5B842]" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
              <User className="w-3 h-3" /> WSTF-GRE-000001
            </button>
            <button type="button" data-testid="verify-example-club" onClick={() => { setQuery("WSTF-CLUB-GRE-0001"); nav("/verify/club/WSTF-CLUB-GRE-0001"); }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-[#2b2512] text-[11px] font-mono text-slate-300 hover:border-[#E5B842]/50 hover:text-[#E5B842]" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
              <Shield className="w-3 h-3" /> WSTF-CLUB-GRE-0001
            </button>
          </div>
        </form>

        {/* ---- CHECKING ---- */}
        {phase === "checking" && (
          <div data-testid="verify-checking" className="rounded-2xl border border-[#2b2512] bg-[#0f0f0f] p-10 text-center my-4">
            <Database className="w-8 h-8 text-[#E5B842] mx-auto mb-3 animate-pulse" />
            <div className="font-mono text-sm uppercase tracking-[0.2em] text-slate-400">Checking WSTF registry…</div>
          </div>
        )}

        {/* ---- RESULT ---- */}
        {phase === "done" && result && (
          <div className="my-5">
            {result.kind === "FOUND" && <FoundCard r={result.data} onCopy={copyShare} copied={copied} />}
            {result.kind === "NOT_FOUND" && (
              <StateCard testid="verify-notfound" Icon={XCircle} tone="slate" title="No Record Found"
                lines={[`No public WSTF registry record matches "${result.id}".`, "A credential that is not found has no public registry entry — this does not by itself indicate fraud."]} />
            )}
            {result.kind === "INVALID" && (
              <StateCard testid="verify-invalid" Icon={Ban} tone="amber" title="Invalid Format"
                lines={[`"${result.id}" does not match a supported WSTF credential format.`, "Expected a Player ID (e.g. WSTF-GRE-000001) or a Club ID (e.g. WSTF-CLUB-GRE-0001)."]} />
            )}
            {result.kind === "UNSUPPORTED" && (
              <StateCard testid="verify-unsupported" Icon={AlertTriangle} tone="amber" title="Type Not Verifiable Here"
                lines={[`"${result.id}" looks like a ${result.which} credential.`, "The Verification Terminal currently validates Player and Club credentials. Other registry entities are not verifiable here yet."]} />
            )}
            {result.kind === "ERROR" && (
              <StateCard testid="verify-error" Icon={AlertTriangle} tone="red" title="Verification Service Unavailable"
                lines={["We could not reach the WSTF registry just now.", "This is a service issue, not a judgement on the credential. Please try again."]} />
            )}
          </div>
        )}

        {/* ---- IDLE GUIDANCE (fixes empty canvas, factual only) ---- */}
        {phase !== "checking" && !result && (
          <div className="grid md:grid-cols-2 gap-5 mt-8" data-testid="verify-guidance">
            <Panel title="Supported Credentials" Icon={BadgeCheck}>
              <ul className="space-y-2.5">
                <SupportRow Icon={User} label="Player" example="WSTF-GRE-000001" />
                <SupportRow Icon={Shield} label="Club" example="WSTF-CLUB-GRE-0001" />
              </ul>
              <p className="mt-4 text-[11px] text-slate-500 leading-relaxed">Other WSTF entities (federations, national teams) exist in the registry but are not verifiable through this terminal yet.</p>
            </Panel>
            <Panel title="How Verification Works" Icon={FileSearch}>
              <ol className="space-y-3">
                <Step n="1" title="Enter or scan a WSTF ID" text="Type a credential ID, or open a credential's QR link." />
                <Step n="2" title="Registry lookup" text="The identifier is checked against the official public WSTF registry." />
                <Step n="3" title="Confirm entity & status" text="The terminal returns the entity, its WSTF ID and its current public status." />
              </ol>
            </Panel>
            <Panel title="WSTF Credential Format" Icon={ScanLine} className="md:col-span-2">
              <div className="grid sm:grid-cols-2 gap-4 font-mono text-xs">
                <div><div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">Player</div><div className="text-slate-200">WSTF-<span className="text-[#E5B842]">COUNTRY</span>-<span className="text-[#E5B842]">NNNNNN</span></div></div>
                <div><div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">Club</div><div className="text-slate-200">WSTF-CLUB-<span className="text-[#E5B842]">COUNTRY</span>-<span className="text-[#E5B842]">NNNN</span></div></div>
              </div>
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Found credential card ---------------- */
function FoundCard({ r, onCopy, copied }) {
  const isPlayer = r.type === "player";
  const e = isPlayer ? r.player : r.club;
  const EntityIcon = isPlayer ? User : Shield;
  const meta = STATUS_META[e.status] || FALLBACK_STATUS;
  const active = e.status === "ACTIVE";
  const wsbfId = isPlayer ? e.wsbf_player_id : e.wsbf_club_id;
  const profile = isPlayer ? `/player/${e.wsbf_player_id}` : `/club/${e.id}`;
  const shareUrl = `${SITE_ORIGIN}/verify/${r.type}/${wsbfId}`;

  return (
    <div data-testid="verify-result" data-verify-state={r.state} className="rounded-2xl foil-border bg-gradient-to-b from-[#14110a] to-[#050505] overflow-hidden">
      {/* headline band */}
      <div className={`flex items-center gap-2 px-6 py-3 border-b ${active ? "border-emerald-500/30 bg-emerald-950/20" : "border-[#2b2512] bg-[#0a0a0a]"}`}>
        <BadgeCheck className={`w-4 h-4 ${active ? "text-emerald-400" : "text-[#E5B842]"}`} />
        <span data-testid="verify-state-label" className={`font-heading font-black uppercase tracking-wider text-sm ${active ? "text-emerald-400" : "text-[#E5B842]"}`}>
          Official WSTF Registry Record
        </span>
      </div>

      <div className="p-6 sm:p-8 grid md:grid-cols-[1fr_auto] gap-8 items-start">
        <div className="min-w-0">
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-14 h-14 rounded-xl border border-[#2b2512] bg-[#0f0f0f] flex items-center justify-center">
              <EntityIcon className="w-7 h-7 text-[#E5B842]" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">{isPlayer ? "Player" : "Club"}</div>
              <div className="font-heading text-2xl sm:text-3xl font-black uppercase text-slate-50 truncate">{isPlayer ? e.display_name : e.name}</div>
              <div className="font-mono text-sm text-[#E5B842] tracking-wider mt-0.5">{wsbfId}</div>
            </div>
          </div>

          {/* CURRENT STATUS — separate, honest signal (valid record ≠ active) */}
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">Current Status</div>
            <div data-testid="verify-status-chip" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-bold uppercase tracking-wide ${TONE[meta.tone]}`}>
              <meta.Icon className="w-4 h-4" /> {meta.word}
            </div>
            <p className="mt-2 text-xs text-slate-400 max-w-md">{meta.note}</p>
          </div>

          {/* public attributes only */}
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {e.country?.name && <Field label="Country" value={e.country.name} Icon={Globe} />}
            {isPlayer && r.club?.name && <Field label="Club" value={r.club.name} />}
            {!isPlayer && e.city && <Field label="City" value={e.city} />}
            {!isPlayer && e.roster && <Field label="Roster" value={`${e.roster.count}/${e.roster.max}`} />}
          </div>

          <Link to={profile} data-testid="verify-profile-link"
            className="inline-flex items-center gap-2 mt-7 px-5 py-2.5 rounded-lg bg-[#E5B842] text-[#000000] font-heading font-bold uppercase text-sm tracking-wide hover:bg-[#C99E30]"
            style={{ transition: "background-color 0.15s ease" }}>
            View official {isPlayer ? "passport" : "charter"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* QR — shareable verification link (registry lookup, not a security token) */}
        <div className="flex flex-col items-center md:border-l md:border-[#2b2512] md:pl-8">
          <img data-testid="verify-qr" src={qrUrl(shareUrl)} alt={`Verification link QR for ${wsbfId}`} className="w-32 h-32 rounded-lg bg-white p-2" loading="lazy" />
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mt-2 text-center">Shareable verification link</div>
          <button type="button" onClick={() => onCopy(shareUrl)} data-testid="verify-copy-link"
            className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-mono text-slate-400 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
            {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy link</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function StateCard({ testid, Icon, tone, title, lines }) {
  return (
    <div data-testid={testid} data-verify-state={testid} className={`rounded-2xl border p-7 sm:p-9 ${TONE[tone]} bg-opacity-20`}>
      <div className="flex items-start gap-4">
        <Icon className="w-9 h-9 shrink-0" />
        <div>
          <div data-testid="verify-state-label" className="font-heading text-2xl font-black uppercase tracking-tight">{title}</div>
          {lines.map((l, i) => <p key={i} className={`mt-2 text-sm ${i === 0 ? "text-slate-200" : "text-slate-400"}`}>{l}</p>)}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, Icon }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{label}</div>
      <div className="text-slate-100 font-semibold flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5 text-slate-500" />}{value}</div>
    </div>
  );
}

function Panel({ title, Icon, children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-[#2b2512] bg-[#0f0f0f] p-6 ${className}`}>
      <h2 className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#E5B842] font-mono font-bold mb-4"><Icon className="w-4 h-4" /> {title}</h2>
      {children}
    </div>
  );
}

function SupportRow({ Icon, label, example }) {
  return (
    <li className="flex items-center gap-3">
      <span className="w-8 h-8 rounded-lg border border-[#2b2512] bg-[#0a0a0a] flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#E5B842]" /></span>
      <div><div className="font-heading font-bold uppercase text-slate-100 text-sm">{label}</div><div className="font-mono text-[11px] text-slate-500">{example}</div></div>
    </li>
  );
}

function Step({ n, title, text }) {
  return (
    <li className="flex gap-3">
      <span className="w-6 h-6 rounded-full border border-[#E5B842]/40 text-[#E5B842] font-heading font-black text-xs flex items-center justify-center shrink-0">{n}</span>
      <div><div className="font-semibold text-slate-100 text-sm">{title}</div><div className="text-xs text-slate-500">{text}</div></div>
    </li>
  );
}
